/**
 * Agent workstation hooks.
 *
 * Pairs with BE routes (all gated on `manage-agents`):
 *   GET   /api/agents/{id}/workstation          → policy, live session, hours, history
 *   PATCH /api/agents/{id}/workstation          → edit the policy
 *   POST  /api/agents/{id}/workstation/start    → { hours, size? }
 *   POST  /api/agents/{id}/workstation/extend   → { hours }
 *   POST  /api/agents/{id}/workstation/stop
 *
 * Every route answers with the same shape as the GET, so each mutation
 * writes its response straight into the cache rather than refetching.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type {
  AgentWorkstation,
  StartWorkstationInput,
  UpdateWorkstationPolicyInput,
} from '@/api/types-bridge'

import { isAgentUuid } from './use-agent-runs'
import { hasHeardWorkstationFrame } from './workstation-frames'

export const workstationQueryKey = (agentId: string) => ['agents', agentId, 'workstation'] as const

/**
 * Poll quickly while a box is starting (to pick up Running), slowly
 * otherwise. Once a `agent.workstation.changed` frame has arrived for the
 * agent the push does that job, so the launching poll falls back to the slow
 * safety poll that covers a dropped frame.
 */
export const WORKSTATION_LAUNCHING_POLL_MS = 15_000
export const WORKSTATION_IDLE_POLL_MS = 60_000

export function useWorkstation(agentId: string | null, { enabled = true } = {}) {
  return useQuery({
    queryKey: workstationQueryKey(agentId ?? ''),
    queryFn: async () => {
      const res = await apiClient<{ data: AgentWorkstation }>(`/api/agents/${agentId}/workstation`)
      return res.data
    },
    enabled: enabled && isAgentUuid(agentId),
    staleTime: 10_000,
    refetchInterval: (query) =>
      query.state.data?.session?.status === 'launching' &&
      !(agentId && hasHeardWorkstationFrame(agentId))
        ? WORKSTATION_LAUNCHING_POLL_MS
        : WORKSTATION_IDLE_POLL_MS,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

const writeThrough = (queryClient: QueryClient, agentId: string, next: AgentWorkstation) => {
  queryClient.setQueryData(workstationQueryKey(agentId), next)
}

function useWorkstationMutation<TInput>(
  agentId: string,
  request: (input: TInput) => { path: string; method: 'PATCH' | 'POST'; body?: unknown },
) {
  const queryClient = useQueryClient()
  return useMutation<AgentWorkstation, Error, TInput>({
    mutationFn: async (input) => {
      const { path, method, body } = request(input)
      const res = await apiClient<{ data: AgentWorkstation }>(
        `/api/agents/${agentId}/workstation${path}`,
        body === undefined ? { method } : { method, body },
      )
      return res.data
    },
    onSuccess: (next) => writeThrough(queryClient, agentId, next),
    // A refused write usually means what the screen shows is stale (hours
    // spent, a session that already ended), so re-read it.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: workstationQueryKey(agentId) })
    },
  })
}

export const useUpdateWorkstationPolicy = (agentId: string) =>
  useWorkstationMutation<UpdateWorkstationPolicyInput>(agentId, (body) => ({
    path: '',
    method: 'PATCH',
    body,
  }))

export const useStartWorkstation = (agentId: string) =>
  useWorkstationMutation<StartWorkstationInput>(agentId, (body) => ({
    path: '/start',
    method: 'POST',
    body,
  }))

export const useExtendWorkstation = (agentId: string) =>
  useWorkstationMutation<{ hours: number }>(agentId, (body) => ({
    path: '/extend',
    method: 'POST',
    body,
  }))

export const useStopWorkstation = (agentId: string) =>
  useWorkstationMutation<void>(agentId, () => ({ path: '/stop', method: 'POST' }))
